/**
 * Hook to manage tab state per conversation
 * Each conversation maintains its own independent tab state
 */

import { useState, useEffect, useCallback } from 'react';

const TAB_STORAGE_KEY = 'conversation-tabs';
const DEFAULT_TAB = 'discover';

interface ConversationTabsState {
  [conversationId: string]: string;
}

/**
 * Hook to manage tab state for a specific conversation
 * @param conversationId - The ID of the conversation
 * @param defaultTab - Default tab ID if no stored state exists
 * @returns [activeTab, setActiveTab] tuple
 */
export function useConversationTabState(
  conversationId: string | null | undefined,
  defaultTab: string = DEFAULT_TAB
): [string, (tabId: string) => void] {
  // Get initial tab from localStorage or use default
  const getInitialTab = useCallback((): string => {
    if (!conversationId || typeof window === 'undefined') {
      return defaultTab;
    }

    try {
      const stored = localStorage.getItem(TAB_STORAGE_KEY);
      if (stored) {
        const tabs: ConversationTabsState = JSON.parse(stored);
        return tabs[conversationId] || defaultTab;
      }
    } catch (error) {
      console.error('Error reading conversation tab state:', error);
    }

    return defaultTab;
  }, [conversationId, defaultTab]);

  const [activeTab, setActiveTabState] = useState<string>(getInitialTab);

  // Update localStorage when tab changes
  const setActiveTab = useCallback(
    (tabId: string) => {
      setActiveTabState(tabId);

      if (!conversationId || typeof window === 'undefined') {
        return;
      }

      try {
        const stored = localStorage.getItem(TAB_STORAGE_KEY);
        const tabs: ConversationTabsState = stored ? JSON.parse(stored) : {};

        tabs[conversationId] = tabId;
        localStorage.setItem(TAB_STORAGE_KEY, JSON.stringify(tabs));
      } catch (error) {
        console.error('Error saving conversation tab state:', error);
      }
    },
    [conversationId]
  );

  // Update tab when conversationId changes
  useEffect(() => {
    if (conversationId) {
      const tab = getInitialTab();
      setActiveTabState(tab);
    }
  }, [conversationId, getInitialTab]);

  return [activeTab, setActiveTab];
}
